import React, { useEffect, useState } from 'react';
import { MbmProvider } from '@amzn/react-arb-tools';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { DEFAULT_LOCALE } from '../constants';
import arbManifest from 'translations/arbManifest';
import { resolveBundleUrlWithFallbacks } from '../helpers';
import ChatPage from "../pages/chat/chat-page";
import ShoulderTapPage from "../pages/shoulder-tap/ShoulderTapPage";
import { useUser } from '../hooks/useUser';
import { useQueryParams } from '../hooks/useQueryParams';
import { isInIframe, isConnectApp, initializeConnectIfNeeded } from '../utils/iframe';
import { isPreProdEnvironment } from '../utils/environment';
import IframeAuthDialog from './IframeAuthDialog';
import CenteredMessage from './CenteredMessage';

const mbmOptions = {
    arbManifest,
    defaultLocalizationContext: new LocalizationContextBuilder()
        .withLocale(DEFAULT_LOCALE)
        .withDefaultLocale(DEFAULT_LOCALE)
        .build(),
    resolveBundleUrl: (
        resourcePackPath: string | undefined,
        locale: string,
        bundle: string
    ) => resolveBundleUrlWithFallbacks(resourcePackPath, locale, bundle),
};

const App = () => {
    const { contactId } = useQueryParams();
    const { user, loading, login, logout, error, checkAuthentication } = useUser();
    const inIframe = isInIframe();
    const inConnect = isConnectApp();
    const [connectInitialized, setConnectInitialized] = useState(false);
    const [authAttempts, setAuthAttempts] = useState(0);
    // Customer state is no longer initialized over HTTP (the /initializeCustomerState
    // endpoint was removed). Session setup now happens via the WebSocket flow, so there
    // is no HTTP init phase here.
    const maxAuthAttempts = 2;

    // Initialize Connect SDK if needed
    useEffect(() => {
        if (inIframe && inConnect && !connectInitialized) {
            initializeConnectIfNeeded()
                .then(() => setConnectInitialized(true))
                .catch(console.error);
        }
    }, [inIframe, inConnect, connectInitialized]);

    // Apply iframe full-width class when in iframe
    useEffect(() => {
        if (inIframe) {
            document.body.classList.add('iframe-full-width');
        } else {
            document.body.classList.remove('iframe-full-width');
        }

        return () => {
            document.body.classList.remove('iframe-full-width');
        };
    }, [inIframe]);
    // Handle authentication based on iframe context
    useEffect(() => {
        if (!loading && !user?.authenticated && !inIframe) {
            console.log('User not authenticated, automatically redirecting to login...');
            setTimeout(() => {
                login();
            }, 500);
        }
    }, [loading, user?.authenticated, login, inIframe]);

    const handleIframeAuthComplete = async () => {
        // Re-check authentication after popup closes
        await checkAuthentication();
        setAuthAttempts(prev => prev + 1);
    };

    // TODO: replace with spinners
    if (loading) {
        return <CenteredMessage message="Loading..." />;
    }

    if (!user?.authenticated) {
        if (inIframe && authAttempts < maxAuthAttempts) {
            return <IframeAuthDialog onAuthComplete={handleIframeAuthComplete} authAttempts={authAttempts} />;
        }
        
        if (inIframe && authAttempts >= maxAuthAttempts) {
            return <CenteredMessage message="Quick Network Hiccup! Try this first: Refresh your browser || Still seeing this? Please Share Contact ID with any helpful context [here](https://t.corp.amazon.com/V2004379452/communication)" />;
        }
        
        return <CenteredMessage message="Redirecting to login..." />;
    }

    return (
        <MbmProvider {...mbmOptions}>
            {window.location.pathname === '/shoulder-tap' && isPreProdEnvironment() ? (
                <ShoulderTapPage user={user} />
            ) : (
                <ChatPage
                    user={user}
                    contactId={contactId}
                />
            )}
        </MbmProvider>
    );
};

export default App;
